import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { differenceInCalendarDays } from "date-fns";
import { Plus, Trash2, Star, Pencil, Check } from "lucide-react";
import DateField from "./DateField";
import { FieldLabel } from "./parts";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const RACE_TYPES = {
  Running: ["5K", "10K", "Half Marathon", "Marathon", "Trail", "Ultra", "Track", "Other"],
  Cycling: ["Road race", "Gran Fondo", "Time trial", "Criterium", "Gravel", "MTB", "Other"],
  Swimming: ["Pool", "Open water", "Other"],
  Triathlon: ["Sprint", "Olympic", "70.3", "Ironman", "Other"],
  default: ["Road", "Trail", "Time trial", "Other"],
};

const PRIORITY = [
  { key: "A", label: "A — Main", hint: "Everything builds toward this" },
  { key: "B", label: "B — Important", hint: "Matters, but not the focus" },
  { key: "C", label: "C — Tune-up", hint: "A training race / test" },
];

const emptyRace = (sport) => ({
  id: crypto.randomUUID(),
  name: "",
  sport,
  race_type: "",
  distance_value: "",
  distance_unit: "km",
  date: "",
  priority: "A",
  goal_kind: "finish",
  target_time: "",
  terrain: "",
  notes: "",
});

function weeksAway(iso) {
  if (!iso) return null;
  const d = differenceInCalendarDays(new Date(iso + "T00:00:00"), new Date());
  if (d < 0) return null;
  return d;
}

export default function RaceManager({ sport, value = [], onChange }) {
  const [draft, setDraft] = useState(null);
  const types = RACE_TYPES[sport] || RACE_TYPES.default;

  const startAdd = () => setDraft(emptyRace(sport));
  const startEdit = (r) => setDraft({ ...r });
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const save = () => {
    const exists = value.some((r) => r.id === draft.id);
    const next = exists ? value.map((r) => (r.id === draft.id ? draft : r)) : [...value, draft];
    onChange(next);
    setDraft(null);
  };

  const remove = (id) => onChange(value.filter((r) => r.id !== id));

  const valid = draft && draft.name.trim() && draft.race_type && draft.date;

  return (
    <div className="space-y-3" data-testid="race-manager">
      <AnimatePresence>
        {value.map((r) => {
          const wa = weeksAway(r.date);
          return (
            <motion.div
              key={r.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              data-testid={`race-card-${r.id}`}
              className={`rounded-2xl border p-4 ${r.priority === "A" ? "border-primary bg-primary/5" : "border-border bg-card"}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${r.priority === "A" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                      {r.priority}
                    </span>
                    <p className="font-heading font-bold text-foreground truncate">{r.name}</p>
                    {r.priority === "A" && <Star size={14} className="text-primary fill-primary shrink-0" />}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {r.race_type}{r.distance_value ? ` · ${r.distance_value}${r.distance_unit}` : ""}
                    {r.target_time && r.goal_kind === "target_time" ? ` · target ${r.target_time}` : ""}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {r.date}{wa != null ? ` · in ${Math.floor(wa / 7)}w ${wa % 7}d` : ""}
                  </p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button data-testid={`race-edit-${r.id}`} onClick={() => startEdit(r)} className="p-2 rounded-lg hover:bg-muted text-muted-foreground"><Pencil size={15} /></button>
                  <button data-testid={`race-remove-${r.id}`} onClick={() => remove(r.id)} className="p-2 rounded-lg hover:bg-muted text-muted-foreground"><Trash2 size={15} /></button>
                </div>
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>

      {draft ? (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="rounded-2xl border border-border bg-card p-5 space-y-4" data-testid="race-form">
          <div>
            <FieldLabel>Race name</FieldLabel>
            <Input data-testid="race-name" value={draft.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Zagreb Half Marathon" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <FieldLabel>Type</FieldLabel>
              <Select value={draft.race_type} onValueChange={(v) => set("race_type", v)}>
                <SelectTrigger data-testid="race-type" className="h-11 rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <FieldLabel>Date</FieldLabel>
              <DateField testid="race-date" value={draft.date} onChange={(v) => set("date", v)} />
            </div>
          </div>
          <div>
            <FieldLabel>Distance <span className="text-muted-foreground font-normal">(optional)</span></FieldLabel>
            <div className="flex gap-2">
              <Input data-testid="race-distance" type="number" value={draft.distance_value} onChange={(e) => set("distance_value", e.target.value)} placeholder="21.1" className="flex-1" />
              <Select value={draft.distance_unit} onValueChange={(v) => set("distance_unit", v)}>
                <SelectTrigger className="h-11 w-24 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="km">km</SelectItem>
                  <SelectItem value="mi">mi</SelectItem>
                  <SelectItem value="m">m</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <FieldLabel>Priority</FieldLabel>
            <div className="grid grid-cols-3 gap-2" data-testid="race-priority">
              {PRIORITY.map((p) => (
                <button
                  key={p.key}
                  data-testid={`race-priority-${p.key}`}
                  onClick={() => set("priority", p.key)}
                  title={p.hint}
                  className={`py-2 rounded-xl border text-sm font-semibold transition-colors ${draft.priority === p.key ? "border-primary bg-primary/10 text-foreground" : "border-border bg-background hover:border-primary/50"}`}
                >
                  {p.key}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-1.5">{PRIORITY.find((p) => p.key === draft.priority)?.hint}</p>
          </div>
          <div>
            <FieldLabel>Goal</FieldLabel>
            <div className="flex gap-2 flex-wrap" data-testid="race-goal">
              {[["finish", "Finish it"], ["target_time", "Target time"], ["placing", "Placing"]].map(([k, l]) => (
                <button key={k} data-testid={`race-goal-${k}`} onClick={() => set("goal_kind", k)}
                  className={`px-4 py-2 rounded-full border text-sm font-medium transition-colors ${draft.goal_kind === k ? "border-primary bg-primary/10 text-foreground" : "border-border bg-background hover:border-primary/50"}`}>
                  {l}
                </button>
              ))}
            </div>
            {draft.goal_kind === "target_time" && (
              <div className="mt-3 flex items-center gap-2">
                <Input data-testid="race-target-time" value={draft.target_time === "I don't know" ? "" : draft.target_time} onChange={(e) => set("target_time", e.target.value)} placeholder="hh:mm:ss (e.g. 01:40:00)" className="flex-1" />
                <button data-testid="race-target-unknown" onClick={() => set("target_time", "I don't know")}
                  className={`text-xs px-3 py-2 rounded-lg border transition-colors ${draft.target_time === "I don't know" ? "border-primary bg-primary/10" : "border-border"}`}>
                  I don't know
                </button>
              </div>
            )}
          </div>
          <div className="flex gap-2 pt-1">
            <Button data-testid="race-save" onClick={save} disabled={!valid} className="flex-1 h-11 rounded-xl font-semibold">
              <Check size={16} className="mr-1" /> Save race
            </Button>
            <Button variant="ghost" onClick={() => setDraft(null)} className="h-11 rounded-xl">Cancel</Button>
          </div>
        </motion.div>
      ) : (
        <button
          data-testid="race-add"
          onClick={startAdd}
          className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl border-2 border-dashed border-border text-muted-foreground hover:border-primary/50 hover:text-foreground transition-colors font-medium"
        >
          <Plus size={18} /> {value.length ? "Add another race" : "Add a race"}
        </button>
      )}
    </div>
  );
}
